package com.daytimegaming.ourdailyfinances.presentation.manageaccounts

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.DragHandle
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.ui.text.font.FontWeight
import sh.calvin.reorderable.rememberReorderableLazyListState
import sh.calvin.reorderable.ReorderableItem
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.domain.model.PlaidItem
import com.daytimegaming.ourdailyfinances.domain.model.Account
import com.daytimegaming.ourdailyfinances.domain.model.RealEstateHolding
import com.daytimegaming.ourdailyfinances.presentation.preview.ManageAccountsPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import org.koin.compose.viewmodel.koinViewModel

sealed interface ReorderableAccountItem {
    val id: String
    val name: String
    val currentBalance: Double?
    val subtitle: String?

    data class Plaid(val account: Account) : ReorderableAccountItem {
        override val id: String get() = account.accountId
        override val name: String get() = account.displayName
        override val currentBalance: Double? get() = account.currentBalance
        override val subtitle: String? get() = listOfNotNull(account.subtype?.uppercase(), account.institutionName).joinToString(" • ")
    }

    data class RealEstate(val holding: RealEstateHolding) : ReorderableAccountItem {
        override val id: String get() = holding.realEstateHoldingId
        override val name: String get() = holding.nickname
        override val currentBalance: Double? get() = holding.netValue
        override val subtitle: String? get() = "REAL ESTATE"
    }
}

@Composable
fun ManageAccountsScreen(
    onNavigateBack: () -> Unit,
    onPlaidTokenReady: (String) -> Unit,
    viewModel: ManageAccountsViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    androidx.compose.runtime.LaunchedEffect(state) {
        val loaded = state as? ManageAccountsScreenState.Loaded
        if (loaded != null && loaded.items.isEmpty()) {
            onNavigateBack()
        }
    }

    androidx.compose.runtime.LaunchedEffect(Unit) {
        viewModel.plaidLinkTokenEvent.collect { token ->
            onPlaidTokenReady(token)
        }
    }

    ManageAccountsScreenContent(
        state = state,
        onNavigateBack = onNavigateBack,
        onConfirmUnlink = { viewModel.confirmUnlink(it) },
        onDismissUnlink = { viewModel.dismissUnlink() },
        onExecuteUnlink = { viewModel.executeUnlink(it) },
        onReorderAccounts = { viewModel.updateAccountOrder(it) },
        onLoginClick = { viewModel.requestUpdateLinkToken(it) },
        onUpdateClick = { viewModel.requestConsentUpdateLinkToken(it) }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ManageAccountsScreenContent(
    state: ManageAccountsScreenState,
    onNavigateBack: () -> Unit,
    onConfirmUnlink: (String) -> Unit,
    onDismissUnlink: () -> Unit,
    onExecuteUnlink: (String) -> Unit,
    onReorderAccounts: (List<String>) -> Unit = {},
    onLoginClick: (String) -> Unit = {},
    onUpdateClick: (String) -> Unit = {},
) {
    val loaded = state as? ManageAccountsScreenState.Loaded
    if (loaded?.unlinkingItemId != null) {
        val item = loaded.items.find { it.itemId == loaded.unlinkingItemId }
        if (item != null) {
            UnlinkConfirmationDialog(
                item = item,
                onDismiss = onDismissUnlink,
                onConfirm = { onExecuteUnlink(item.itemId) },
            )
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Manage Accounts") },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                    }
                },
            )
        },
    ) { innerPadding ->
        when (val s = state) {
            is ManageAccountsScreenState.Loading -> {
                Box(
                    modifier = Modifier.fillMaxSize().padding(innerPadding),
                    contentAlignment = Alignment.Center,
                ) {
                    CircularProgressIndicator()
                }
            }
            is ManageAccountsScreenState.Error -> {
                Box(
                    modifier = Modifier.fillMaxSize().padding(innerPadding),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(text = s.message, color = MaterialTheme.colorScheme.error)
                }
            }
            is ManageAccountsScreenState.Loaded -> {
                val accountsList = s.accounts
                val holdingsList = s.realEstateHoldings
                
                val combinedItems = androidx.compose.runtime.remember(accountsList, holdingsList, s.accountOrder) {
                    val list = accountsList.map { ReorderableAccountItem.Plaid(it) } +
                               holdingsList.map { ReorderableAccountItem.RealEstate(it) }
                    
                    if (s.accountOrder.isNotEmpty()) {
                        list.sortedWith { a1, a2 ->
                            val idx1 = s.accountOrder.indexOf(a1.id)
                            val idx2 = s.accountOrder.indexOf(a2.id)
                            when {
                                idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                                idx1 != -1 -> -1
                                idx2 != -1 -> 1
                                else -> 0
                            }
                        }
                    } else {
                        list
                    }
                }

                val lazyListState = rememberLazyListState()
                val reorderableState = rememberReorderableLazyListState(lazyListState) { from, to ->
                    val fromIndex = combinedItems.indexOfFirst { it.id == from.key }
                    val toIndex = combinedItems.indexOfFirst { it.id == to.key }
                    if (fromIndex != -1 && toIndex != -1) {
                        val updated = combinedItems.toMutableList().apply {
                            add(toIndex, removeAt(fromIndex))
                        }
                        onReorderAccounts(updated.map { it.id })
                    }
                }

                LazyColumn(
                    state = lazyListState,
                    modifier = Modifier.fillMaxSize().padding(innerPadding),
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                    contentPadding = PaddingValues(16.dp),
                ) {
                    item {
                        Text(
                            text = "Linked Institutions",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurface,
                            modifier = Modifier.padding(bottom = 4.dp)
                        )
                    }

                    items(s.items, key = { it.itemId }) { item ->
                        GlassCard(modifier = Modifier.fillMaxWidth()) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Column(
                                    modifier = Modifier.weight(1f),
                                    verticalArrangement = Arrangement.spacedBy(4.dp)
                                ) {
                                    Text(
                                        text = item.institutionName,
                                        style = MaterialTheme.typography.titleMedium,
                                        color = MaterialTheme.colorScheme.onSurface,
                                    )
                                    if (item.loginRequired) {
                                        Text(
                                            text = "This account is requesting for you to reauthenticate. Please tap the Login button to begin.",
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                                        )
                                    }
                                    if (item.updateRequired) {
                                        Text(
                                            text = "This account is requesting additional consent for permissions. Please tap the Update button to begin.",
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                                        )
                                    }
                                }

                                Spacer(modifier = Modifier.width(16.dp))

                                Row(
                                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                ) {
                                     if (item.loginRequired) {
                                         OutlinedButton(
                                             onClick = { onLoginClick(item.itemId) },
                                         ) {
                                             Text("Login")
                                         }
                                     }
                                     if (item.updateRequired) {
                                         OutlinedButton(
                                             onClick = { onUpdateClick(item.itemId) },
                                         ) {
                                             Text("Update")
                                         }
                                     }
                                     OutlinedButton(
                                        onClick = { onConfirmUnlink(item.itemId) },
                                        colors = ButtonDefaults.outlinedButtonColors(
                                            contentColor = MaterialTheme.colorScheme.error,
                                        ),
                                    ) {
                                        Text("Unlink")
                                    }
                                }
                            }
                        }
                    }

                    if (combinedItems.isNotEmpty()) {
                        item {
                            Spacer(modifier = Modifier.height(16.dp))
                            Text(
                                text = "Accounts Order",
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurface,
                                modifier = Modifier.padding(bottom = 4.dp)
                            )
                        }

                        items(combinedItems, key = { it.id }) { item ->
                            ReorderableItem(state = reorderableState, key = item.id) { isDragging ->
                                val elevation by animateDpAsState(if (isDragging) 8.dp else 0.dp)
                                Surface(
                                    shadowElevation = elevation,
                                    shape = RoundedCornerShape(12.dp),
                                    color = if (isDragging) {
                                        MaterialTheme.colorScheme.surfaceVariant
                                    } else {
                                        MaterialTheme.colorScheme.surface
                                    },
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .border(
                                            width = 1.dp,
                                            color = if (isDragging) {
                                                MaterialTheme.colorScheme.primary.copy(alpha = 0.5f)
                                            } else {
                                                MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f)
                                            },
                                            shape = RoundedCornerShape(12.dp)
                                        )
                                ) {
                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(horizontal = 16.dp, vertical = 12.dp),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Column(modifier = Modifier.weight(1f)) {
                                            Text(
                                                text = item.name,
                                                style = MaterialTheme.typography.titleMedium,
                                                fontWeight = FontWeight.SemiBold,
                                                color = MaterialTheme.colorScheme.onSurface
                                            )
                                            Row(
                                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                val subtitle = item.subtitle
                                                if (!subtitle.isNullOrEmpty()) {
                                                    Text(
                                                        text = subtitle,
                                                        style = MaterialTheme.typography.bodySmall,
                                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                                    )
                                                }
                                                val balance = item.currentBalance
                                                if (balance != null) {
                                                    val balanceText = if (subtitle.isNullOrEmpty()) "" else "•  "
                                                    Text(
                                                        text = "${balanceText}$${balance.formatAmount()}",
                                                        style = MaterialTheme.typography.bodySmall,
                                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                                    )
                                                }
                                            }
                                        }
                                        Icon(
                                            imageVector = Icons.Default.DragHandle,
                                            contentDescription = "Drag to reorder",
                                            tint = MaterialTheme.colorScheme.onSurfaceVariant,
                                            modifier = Modifier
                                                .draggableHandle()
                                                .size(24.dp)
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@PreviewLightDark
@Composable
fun ManageAccountsScreenPreview(
    @PreviewParameter(ManageAccountsPreviewParameterProvider::class) state: ManageAccountsScreenState
) {
    AppTheme(
        darkTheme = isSystemInDarkTheme(),
        dynamicColor = false,
    ) {
        Surface {
            ManageAccountsScreenContent(
                state = state,
                onNavigateBack = {},
                onConfirmUnlink = {},
                onDismissUnlink = {},
                onExecuteUnlink = {}
            )
        }
    }
}

@Composable
private fun UnlinkConfirmationDialog(
    item: PlaidItem,
    onDismiss: () -> Unit,
    onConfirm: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Unlink ${item.institutionName}?") },
        text = { Text("This will remove all accounts and transactions linked through this connection.") },
        confirmButton = {
            TextButton(
                onClick = onConfirm,
                colors = ButtonDefaults.textButtonColors(
                    contentColor = MaterialTheme.colorScheme.error,
                ),
            ) {
                Text("Unlink")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        },
    )
}
